import { Injectable, computed, effect, inject, signal } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel
} from '@microsoft/signalr';
import { Subject } from 'rxjs';

import { AuthService } from './auth.service';
import { TokenBagService } from './token-bag.service';
import { environment } from '../../../environments/environment';
import { resolveTenantBaseUrl } from './your-events.service';
import type { BroadcastAction, BroadcastMessage } from '../models/broadcast.models';
import type {
  CallAcceptedNotice,
  CallMediaType,
  CallRoomNotice,
  IncomingCallNotice
} from '../models/video.models';

/**
 * Realtime chat / presence client for the mobile app — analogous to
 * `apps/web/src/app/core/services/realtime.service.ts`. Connects to the
 * Engagement service SignalR hub (`/hubs/engagement`) and:
 *
 *  - Invokes <c>JoinEventGroup(eventId)</c> as soon as the active token
 *    bag entry has an event id. This is what flips the user to "online"
 *    in the event's chat presence set (see
 *    <c>vEvents.Engagement.Services.PresenceTracker.TrackJoinEvent</c>),
 *    triggers the <c>UserOnline</c> broadcast, and lets web clients
 *    watching the same event observe the mobile user as online.
 *  - Listens to <c>UserOnline</c> / <c>UserOffline</c> / <c>UserInCall</c>
 *    / <c>UserAvailable</c> so consumers can render the presence chip
 *    next to chat participants without polling.
 *  - Listens to <c>ReceiveMessage</c>, <c>ChannelUpdated</c>,
 *    <c>ChatError</c> so the inbox / DM thread surfaces update in real
 *    time when other members send messages.
 *  - Exposes <c>joinChannel</c>, <c>leaveChannel</c>, <c>sendMessage</c>,
 *    <c>markRead</c> wrappers for components to consume.
 *
 * The connection is kept alive across tenant switches: when the active
 * tenant changes we tear down and re-open the hub against the new
 * tenant's bearer; when the active event id changes we re-issue
 * <c>JoinEventGroup</c> for the new event.
 */
const HUB_PATH = '/hubs/engagement';
const RECONNECT_DELAYS_MS = [0, 2_000, 5_000, 10_000, 30_000];

export interface ChatHubMessage {
  id: number;
  channelId: string;
  senderId: string;
  senderName: string;
  senderRole: string;
  senderAvatar?: string | null;
  body: string;
  isHidden?: boolean;
  createdAt: string;
}

export interface ChannelUpdate {
  channelId: string;
  lastMessage?: {
    id: number;
    senderId: string;
    body: string;
    senderName: string;
    createdAt: string;
  };
}

export interface PresenceState {
  /** Set of user ids currently online in the joined event. */
  online: ReadonlySet<string>;
  /** Set of user ids currently in a call. */
  inCall: ReadonlySet<string>;
}

/**
 * Real-time room-Wall (Q&A) event. The server fans these out to the
 * `Wall_{roomId}` group; mobile mirrors the web venue's wall-widget which
 * reloads the feed on each event (post-deleted carries the post id so the
 * list can drop it without a refetch).
 */
export interface WallEvent {
  kind: 'post-created' | 'comment-added' | 'post-pinned' | 'post-deleted' | 'question-answered';
  postId?: number;
}

@Injectable({ providedIn: 'root' })
export class EngagementHubService {
  private readonly auth = inject(AuthService);
  private readonly tokenBag = inject(TokenBagService);

  private connection: HubConnection | null = null;
  private connectedTenantId: number | null = null;
  private joinedEventId: number | null = null;

  // Group memberships we want to hold for the lifetime of the surfaces that
  // requested them. SignalR group membership is per-connection, so it is lost
  // whenever the connection is torn down — both on an auto-reconnect AND on the
  // manual reconnect we trigger when the active tenant/event changes (e.g. a
  // session room calls syncActiveEvent on open). We re-issue every desired join
  // after each (re)connect so a recipient keeps receiving ReceiveMessage on the
  // channel they have open instead of going silently deaf after an event sync.
  private readonly desiredChannels = new Set<string>();
  private readonly desiredRoomChats = new Set<string>();
  private readonly desiredWalls = new Set<string>();
  // Room-scoped broadcast groups (`Room_{roomId}`). Global broadcasts ride
  // the event group joined by JoinEventGroup; a room-targeted broadcast only
  // reaches connections that have explicitly joined the room group.
  private readonly desiredBroadcastRooms = new Set<string>();

  // Reconnect token — bumped on every reconnect to short-circuit stale
  // start() callbacks if the user tenant-switches mid-handshake.
  private reconnectToken = 0;

  private readonly _isConnected = signal(false);
  readonly isConnected = this._isConnected.asReadonly();

  private readonly _online = signal<ReadonlySet<string>>(new Set<string>());
  private readonly _inCall = signal<ReadonlySet<string>>(new Set<string>());
  readonly presence = computed<PresenceState>(() => ({
    online: this._online(),
    inCall: this._inCall()
  }));

  /** Emits every chat message routed to a channel this client has joined. */
  readonly messages$ = new Subject<ChatHubMessage>();
  /** Emits whenever a channel the user is a member of receives an update. */
  readonly channelUpdates$ = new Subject<ChannelUpdate>();
  /** Emits chat error notices from the hub (mute/ban/terms). */
  readonly errors$ = new Subject<string>();
  /** Emits room-Wall (Q&A) feed change events for the joined wall group. */
  readonly wallEvents$ = new Subject<WallEvent>();
  /**
   * Emits admin broadcast messages routed to the joined event (or room)
   * group. Surfaced as an in-app toast by `BroadcastToastComponent`. The
   * connection is already in `Event_{eventId}` via JoinEventGroup, so
   * global broadcasts arrive with no extra subscription.
   */
  readonly broadcasts$ = new Subject<BroadcastMessage>();

  // ---------------------------------------------------------------------
  // 1:1 video/audio call signalling (Phase 26C parity).
  //
  // The same EngagementHub carries call ring/accept/decline/end events as
  // the web venue. The LiveKit *credentials* for the negotiated room come
  // from VideoHubService.joinPrivateCallRoom; these streams only carry the
  // signalling so a higher-level CallService can drive the call UI.
  // ---------------------------------------------------------------------

  /** A peer is ringing us — show the incoming-call sheet. */
  readonly incomingCall$ = new Subject<IncomingCallNotice>();
  /** The peer we rang accepted — the caller now joins the LiveKit room. */
  readonly callAccepted$ = new Subject<CallAcceptedNotice>();
  /** The peer we rang declined. */
  readonly callDeclined$ = new Subject<CallRoomNotice>();
  /** The caller cancelled before we answered. */
  readonly callCancelled$ = new Subject<CallRoomNotice>();
  /** Either party ended an in-progress call. */
  readonly callEnded$ = new Subject<CallRoomNotice>();

  constructor() {
    // Track the active tenant + event id. Every time either changes we
    // re-open the hub (tenant switch invalidates the JWT) or re-issue
    // JoinEventGroup (event switch). The effect is the only path that
    // mutates `connection`, so the lifecycle stays single-threaded.
    effect(() => {
      const active = this.tokenBag.active();
      if (!active) {
        void this.disconnect();
        return;
      }
      const targetEventId = active.eventId ?? null;
      const tenantChanged = active.tenantId !== this.connectedTenantId;
      const eventChanged = targetEventId !== this.joinedEventId;
      if (tenantChanged) {
        void this.reconnect(active.tenantId, targetEventId);
        return;
      }
      if (eventChanged) {
        void this.rejoinEvent(targetEventId);
      }
    });
  }

  // ---------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------

  /** Opens the connection if it is not already open (idempotent). */
  async ensureConnected(): Promise<void> {
    const active = this.tokenBag.getActive();
    if (!active) return;
    if (active.tenantId === this.connectedTenantId &&
        this.connection?.state === HubConnectionState.Connected) {
      return;
    }
    await this.reconnect(active.tenantId, active.eventId ?? null);
  }

  /** Subscribe to a chat channel group so ReceiveMessage events flow. */
  async joinChannel(channelId: string): Promise<void> {
    // Remember the desire even if we aren't connected yet — rejoinGroups()
    // re-issues it once the connection is (re)established.
    this.desiredChannels.add(channelId);
    const conn = await this.getConnectedConnection();
    if (!conn) return;
    try {
      await conn.invoke('JoinChannel', channelId);
    } catch (err) {
      console.warn('[EngagementHubService] JoinChannel failed:', err);
    }
  }

  /**
   * Join a room/stage chat group. Room chat messages are broadcast to the
   * server-side `Chat_Room_{roomId}` group (see EngagementHub.SendMessage),
   * NOT the `Chat_Channel_{guid}` group `joinChannel` subscribes to — so a
   * room/stage chat must call this to actually receive live messages. This
   * mirrors the web venue's `chatService.joinRoomChat(roomId)`.
   */
  async joinRoomChat(roomId: string): Promise<void> {
    this.desiredRoomChats.add(roomId);
    const conn = await this.getConnectedConnection();
    if (!conn) return;
    try {
      await conn.invoke('JoinRoomChat', roomId);
    } catch (err) {
      console.warn('[EngagementHubService] JoinRoomChat failed:', err);
    }
  }

  /**
   * Join a room-targeted broadcast group (`Room_{roomId}`) so a broadcast
   * scoped to that room reaches this client. Mirrors the web venue's
   * `realtimeService.joinRoomGroup(roomId)` (EngagementHub.JoinRoomGroup).
   * Global (event-wide) broadcasts do NOT require this — they ride the
   * event group already joined by JoinEventGroup.
   */
  async joinRoomGroup(roomId: string): Promise<void> {
    if (!roomId) return;
    this.desiredBroadcastRooms.add(roomId);
    const conn = await this.getConnectedConnection();
    if (!conn) return;
    try {
      await conn.invoke('JoinRoomGroup', roomId);
    } catch (err) {
      console.warn('[EngagementHubService] JoinRoomGroup failed:', err);
    }
  }

  /** Leave a room-targeted broadcast group (`Room_{roomId}`). */
  async leaveRoomGroup(roomId: string): Promise<void> {
    this.desiredBroadcastRooms.delete(roomId);
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    try {
      await conn.invoke('LeaveRoomGroup', roomId);
    } catch (err) {
      console.warn('[EngagementHubService] LeaveRoomGroup failed:', err);
    }
  }

  /**
   * Join the room Wall (Q&A) group so PostCreated / CommentAdded /
   * PostPinned / PostDeleted events flow. The server group is
   * `Wall_{roomId}` (EngagementHub.JoinWall) — the same wire the web
   * venue's wall-widget uses.
   */
  async joinWall(roomId: string): Promise<void> {
    this.desiredWalls.add(roomId);
    const conn = await this.getConnectedConnection();
    if (!conn) return;
    try {
      await conn.invoke('JoinWall', roomId);
    } catch (err) {
      console.warn('[EngagementHubService] JoinWall failed:', err);
    }
  }

  /** Leave the room Wall (Q&A) group. */
  async leaveWall(roomId: string): Promise<void> {
    this.desiredWalls.delete(roomId);
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    try {
      await conn.invoke('LeaveWall', roomId);
    } catch (err) {
      console.warn('[EngagementHubService] LeaveWall failed:', err);
    }
  }

  /** Unsubscribe from a chat channel group. */
  async leaveChannel(channelId: string): Promise<void> {
    this.desiredChannels.delete(channelId);
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    try {
      await conn.invoke('LeaveChannel', channelId);
    } catch (err) {
      console.warn('[EngagementHubService] LeaveChannel failed:', err);
    }
  }

  /**
   * Send a chat message via the engagement hub. The server persists the
   * row and fans out `ReceiveMessage` to the channel group + per-member
   * `ChannelUpdated` events. Returns true when the invoke succeeded.
   */
  async sendMessage(
    channelId: string,
    body: string,
    senderName: string,
    senderRole: string,
    senderAvatar?: string | null
  ): Promise<boolean> {
    const conn = await this.getConnectedConnection();
    if (!conn) return false;
    try {
      await conn.invoke(
        'SendMessage',
        channelId,
        body,
        senderName,
        senderRole,
        senderAvatar ?? null
      );
      return true;
    } catch (err) {
      console.warn('[EngagementHubService] SendMessage failed:', err);
      return false;
    }
  }

  /** Mark a channel as read up to now. */
  async markRead(channelId: string): Promise<void> {
    const conn = await this.getConnectedConnection();
    if (!conn) return;
    try {
      await conn.invoke('MarkRead', channelId);
    } catch (err) {
      console.warn('[EngagementHubService] MarkRead failed:', err);
    }
  }

  /**
   * Send a typing indicator on the channel. No-op when the hub is not
   * connected — typing is best-effort UX, never blocks compose.
   */
  async typing(channelId: string, isTyping: boolean): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    try {
      await conn.invoke('TypingIndicator', channelId, isTyping);
    } catch {
      // Swallow — typing is non-essential.
    }
  }

  // ---------------------------------------------------------------------
  // 1:1 call signalling
  // ---------------------------------------------------------------------

  /**
   * Ring `targetUserId` for a 1:1 call in `roomName`. The server marks the
   * caller in-call and pushes `IncomingCall` to the target's connections.
   * Throws when the target is already in a call (surfaced to the UI).
   */
  async initiateCall(targetUserId: string, callType: CallMediaType, roomName: string): Promise<void> {
    const conn = await this.getConnectedConnection();
    if (!conn) throw new Error('Not connected to the event — try again in a moment.');
    await conn.invoke('InitiateCall', targetUserId, callType, roomName);
  }

  /** Accept an incoming call; the server notifies the caller via `CallAccepted`. */
  async acceptCall(roomName: string, callerId: string): Promise<void> {
    const conn = await this.getConnectedConnection();
    if (!conn) return;
    try {
      await conn.invoke('AcceptCall', roomName, callerId);
    } catch (err) {
      console.warn('[EngagementHubService] AcceptCall failed:', err);
    }
  }

  /** Decline an incoming call; the server notifies the caller via `CallDeclined`. */
  async declineCall(roomName: string, callerId: string): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    try {
      await conn.invoke('DeclineCall', roomName, callerId);
    } catch (err) {
      console.warn('[EngagementHubService] DeclineCall failed:', err);
    }
  }

  /** Cancel an outgoing call we placed but the peer hasn't answered yet. */
  async cancelCall(roomName: string): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    try {
      await conn.invoke('CancelCall', roomName);
    } catch (err) {
      console.warn('[EngagementHubService] CancelCall failed:', err);
    }
  }

  /** End an in-progress call; the server fans `CallEnded` to both parties. */
  async endCall(roomName: string): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    try {
      await conn.invoke('EndCall', roomName);
    } catch (err) {
      console.warn('[EngagementHubService] EndCall failed:', err);
    }
  }

  // ---------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------

  private async getConnectedConnection(): Promise<HubConnection | null> {
    await this.ensureConnected();
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return null;
    }
    return this.connection;
  }

  private async disconnect(): Promise<void> {
    this.reconnectToken += 1;
    const conn = this.connection;
    this.connection = null;
    this.connectedTenantId = null;
    this.joinedEventId = null;
    this._isConnected.set(false);
    this._online.set(new Set<string>());
    this._inCall.set(new Set<string>());
    if (!conn) return;
    try {
      await conn.stop();
    } catch {
      // Idempotent.
    }
  }

  private async reconnect(tenantId: number, eventId: number | null): Promise<void> {
    await this.disconnect();
    this.connectedTenantId = tenantId;
    const ourToken = ++this.reconnectToken;

    const active = this.tokenBag.getByTenantId(tenantId);
    if (!active) return;

    const baseUrl = resolveTenantBaseUrl(environment.apiUrl, active.tenantSlug);
    const builder = new HubConnectionBuilder()
      .withUrl(`${baseUrl}${HUB_PATH}`, {
        accessTokenFactory: () => this.auth.getToken() ?? '',
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) =>
          RECONNECT_DELAYS_MS[Math.min(ctx.previousRetryCount, RECONNECT_DELAYS_MS.length - 1)]
      })
      .configureLogging(environment.production ? LogLevel.Warning : LogLevel.Information);

    const conn = builder.build();
    this.attachHandlers(conn);
    this.connection = conn;

    try {
      await conn.start();
    } catch (err) {
      console.warn('[EngagementHubService] hub start failed:', err);
      return;
    }

    // If a newer reconnect started while we were handshaking, bail out.
    if (ourToken !== this.reconnectToken) return;

    this._isConnected.set(true);
    if (eventId != null) {
      await this.joinEventGroup(eventId);
    }
    // Re-issue chat/room/wall joins on this fresh connection — group
    // membership does not survive a connection teardown.
    await this.rejoinGroups();
  }

  /**
   * Re-issue every desired group join on the current connection. Called after
   * each (re)connect so channel/room/wall subscriptions survive a connection
   * teardown (auto-reconnect or the manual reconnect we trigger on a tenant /
   * event switch). Without this a recipient stops receiving live messages on a
   * channel they still have open.
   */
  private async rejoinGroups(): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    for (const channelId of this.desiredChannels) {
      try {
        await conn.invoke('JoinChannel', channelId);
      } catch (err) {
        console.warn('[EngagementHubService] rejoin channel failed:', err);
      }
    }
    for (const roomId of this.desiredRoomChats) {
      try {
        await conn.invoke('JoinRoomChat', roomId);
      } catch (err) {
        console.warn('[EngagementHubService] rejoin room chat failed:', err);
      }
    }
    for (const roomId of this.desiredWalls) {
      try {
        await conn.invoke('JoinWall', roomId);
      } catch (err) {
        console.warn('[EngagementHubService] rejoin wall failed:', err);
      }
    }
    for (const roomId of this.desiredBroadcastRooms) {
      try {
        await conn.invoke('JoinRoomGroup', roomId);
      } catch (err) {
        console.warn('[EngagementHubService] rejoin room group failed:', err);
      }
    }
  }

  private async rejoinEvent(eventId: number | null): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) {
      this.joinedEventId = eventId;
      return;
    }
    // Leave the previous event group so presence updates from there
    // stop firing for this connection.
    if (this.joinedEventId != null && this.joinedEventId !== eventId) {
      try {
        await conn.invoke('LeaveEventGroup', this.joinedEventId.toString());
      } catch (err) {
        console.warn('[EngagementHubService] LeaveEventGroup failed:', err);
      }
    }
    if (eventId == null) {
      this.joinedEventId = null;
      this._online.set(new Set<string>());
      this._inCall.set(new Set<string>());
      return;
    }
    await this.joinEventGroup(eventId);
  }

  private async joinEventGroup(eventId: number): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    try {
      await conn.invoke('JoinEventGroup', eventId.toString());
      this.joinedEventId = eventId;
    } catch (err) {
      console.warn('[EngagementHubService] JoinEventGroup failed:', err);
    }
  }

  private attachHandlers(conn: HubConnection): void {
    // Presence
    conn.on('UserOnline', (userId: string) => {
      if (!userId) return;
      const next = new Set(this._online());
      next.add(String(userId));
      this._online.set(next);
    });
    conn.on('UserOffline', (userId: string) => {
      if (!userId) return;
      const next = new Set(this._online());
      next.delete(String(userId));
      this._online.set(next);
    });
    conn.on('UserInCall', (userId: string) => {
      if (!userId) return;
      const next = new Set(this._inCall());
      next.add(String(userId));
      this._inCall.set(next);
    });
    conn.on('UserAvailable', (userId: string) => {
      if (!userId) return;
      const next = new Set(this._inCall());
      next.delete(String(userId));
      this._inCall.set(next);
    });

    // Chat
    conn.on('ReceiveMessage', (raw: unknown) => {
      const msg = parseChatMessage(raw);
      if (msg) this.messages$.next(msg);
    });
    conn.on('ChannelUpdated', (raw: unknown) => {
      const update = parseChannelUpdate(raw);
      if (update) this.channelUpdates$.next(update);
    });
    conn.on('ChatError', (text: unknown) => {
      if (typeof text === 'string' && text.length > 0) {
        this.errors$.next(text);
      }
    });

    // Room Wall (Q&A)
    conn.on('PostCreated', () => this.wallEvents$.next({ kind: 'post-created' }));
    conn.on('CommentAdded', () => this.wallEvents$.next({ kind: 'comment-added' }));
    conn.on('PostPinned', () => this.wallEvents$.next({ kind: 'post-pinned' }));
    conn.on('PostDeleted', (raw: unknown) => {
      const postId = readPostId(raw);
      this.wallEvents$.next({ kind: 'post-deleted', postId });
    });
    conn.on('QuestionAnswered', (raw: unknown) => {
      const postId = readPostId(raw);
      this.wallEvents$.next({ kind: 'question-answered', postId });
    });

    // Admin broadcasts — fanned to Event_{eventId} (global) or Room_{roomId}
    // (room scope). The mobile client is already in the event group, so
    // global broadcasts arrive here without any extra subscription.
    conn.on('ReceiveBroadcast', (raw: unknown) => {
      const message = parseBroadcast(raw);
      if (message) this.broadcasts$.next(message);
    });

    // 1:1 call signalling — drives CallService / the global call overlay.
    conn.on('IncomingCall', (raw: unknown) => {
      const notice = parseIncomingCall(raw);
      if (notice) this.incomingCall$.next(notice);
    });
    conn.on('CallAccepted', (raw: unknown) => {
      const r = asRecord(raw);
      const roomName = r ? pickString(r['roomName'], r['RoomName']) : undefined;
      if (!roomName) return;
      this.callAccepted$.next({
        roomName,
        accepterId: (r && pickString(r['accepterId'], r['AccepterId'])) ?? ''
      });
    });
    conn.on('CallDeclined', (raw: unknown) => {
      const roomName = roomNameOf(raw);
      if (roomName) this.callDeclined$.next({ roomName });
    });
    conn.on('CallCancelled', (raw: unknown) => {
      const roomName = roomNameOf(raw);
      if (roomName) this.callCancelled$.next({ roomName });
    });
    conn.on('CallEnded', (raw: unknown) => {
      const roomName = roomNameOf(raw);
      if (roomName) this.callEnded$.next({ roomName });
    });

    conn.onreconnected(async () => {
      this._isConnected.set(true);
      // Rejoin the event group — server-side reconnect does not re-add
      // the connection to the group, only to the user-{userId} group.
      if (this.joinedEventId != null) {
        try {
          await conn.invoke('JoinEventGroup', this.joinedEventId.toString());
        } catch (err) {
          console.warn('[EngagementHubService] rejoin event after reconnect failed:', err);
        }
      }
      // Rejoin chat/room/wall groups too, or the user goes deaf on threads
      // they still have open after a transient drop.
      await this.rejoinGroups();
    });
    conn.onclose(() => {
      this._isConnected.set(false);
      this._online.set(new Set<string>());
      this._inCall.set(new Set<string>());
    });
  }
}

function parseBroadcast(raw: unknown): BroadcastMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  // Accept both camelCase (default SignalR JSON) and PascalCase (some
  // Azure SignalR / older server builds) — mirrors the web toast client.
  const id = pickString(r['id'], r['Id']);
  const title = pickString(r['title'], r['Title']);
  const body = pickString(r['body'], r['Body']);
  if (!id || !title || !body) return null;

  const rawActions = (r['actions'] ?? r['Actions']) as unknown;
  const actions: BroadcastAction[] = Array.isArray(rawActions)
    ? rawActions.flatMap((a) => {
        if (!a || typeof a !== 'object') return [];
        const ar = a as Record<string, unknown>;
        const actionType = pickString(ar['actionType'], ar['ActionType']);
        const label = pickString(ar['label'], ar['Label']);
        const payload = pickString(ar['payload'], ar['Payload']);
        if (!actionType || !label) return [];
        return [{ actionType, label, payload: payload ?? '' }];
      })
    : [];

  const durationRaw = r['displayDuration'] ?? r['DisplayDuration'];
  const displayDuration = Number(durationRaw);

  return {
    id,
    title,
    body,
    actions,
    timestamp: pickString(r['timestamp'], r['Timestamp']) ?? new Date().toISOString(),
    displayDuration: Number.isFinite(displayDuration) && displayDuration > 0 ? displayDuration : undefined
  };
}

function pickString(...candidates: unknown[]): string | undefined {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.length > 0) return candidate;
  }
  return undefined;
}

function asRecord(raw: unknown): Record<string, unknown> | null {
  return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
}

/** Extract the `roomName` from a decline/cancel/end payload (either casing). */
function roomNameOf(raw: unknown): string | undefined {
  const r = asRecord(raw);
  return r ? pickString(r['roomName'], r['RoomName']) : undefined;
}

function parseIncomingCall(raw: unknown): IncomingCallNotice | null {
  const r = asRecord(raw);
  if (!r) return null;
  const callerId = pickString(r['callerId'], r['CallerId']);
  const roomName = pickString(r['roomName'], r['RoomName']);
  if (!callerId || !roomName) return null;
  const callTypeRaw = pickString(r['callType'], r['CallType']);
  return {
    callerId,
    callerName: pickString(r['callerName'], r['CallerName']) ?? 'Someone',
    callerRole: pickString(r['callerRole'], r['CallerRole']) ?? 'User',
    callType: callTypeRaw === 'audio' ? 'audio' : 'video',
    roomName
  };
}

function readPostId(raw: unknown): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const id = Number(r['postId'] ?? r['wallPostId'] ?? r['id']);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

function pickAvatar(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (candidate != null) return String(candidate);
  }
  return null;
}

function parseChatMessage(raw: unknown): ChatHubMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  // Accept both camelCase (default SignalR JSON) and PascalCase (some
  // Azure SignalR / older server builds) so a web-sent message is never
  // silently dropped on mobile — mirrors parseBroadcast / parseChannelUpdate.
  const id = Number(r['id'] ?? r['Id']);
  const channelIdRaw = r['channelId'] ?? r['ChannelId'];
  const channelId = typeof channelIdRaw === 'string' ? channelIdRaw : null;
  const bodyRaw = r['body'] ?? r['Body'];
  const body = typeof bodyRaw === 'string' ? bodyRaw : '';
  if (!Number.isFinite(id) || !channelId) return null;
  return {
    id,
    channelId,
    senderId: String(r['senderId'] ?? r['SenderId'] ?? ''),
    senderName: String(r['senderName'] ?? r['SenderName'] ?? ''),
    senderRole: String(r['senderRole'] ?? r['SenderRole'] ?? ''),
    senderAvatar: pickAvatar(r['senderAvatar'], r['SenderAvatar']),
    body,
    isHidden: Boolean(r['isHidden'] ?? r['IsHidden']),
    createdAt: String(r['createdAt'] ?? r['CreatedAt'] ?? new Date().toISOString())
  };
}

function parseChannelUpdate(raw: unknown): ChannelUpdate | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const channelIdRaw = r['channelId'] ?? r['ChannelId'];
  const channelId = typeof channelIdRaw === 'string' ? channelIdRaw : null;
  if (!channelId) return null;
  const lastRaw = r['lastMessage'] ?? r['LastMessage'];
  let lastMessage: ChannelUpdate['lastMessage'] | undefined;
  if (lastRaw && typeof lastRaw === 'object') {
    const lm = lastRaw as Record<string, unknown>;
    const id = Number(lm['id'] ?? lm['Id']);
    if (Number.isFinite(id)) {
      lastMessage = {
        id,
        senderId: String(lm['senderId'] ?? lm['SenderId'] ?? ''),
        body: String(lm['body'] ?? lm['Body'] ?? ''),
        senderName: String(lm['senderName'] ?? lm['SenderName'] ?? ''),
        createdAt: String(lm['createdAt'] ?? lm['CreatedAt'] ?? new Date().toISOString())
      };
    }
  }
  return { channelId, lastMessage };
}
